// StarSiloButton — universal "favorite" button for logged-in users (Feb 17, 2026)
// Used on PublicSilo headers, Silo Wars BattleCards, Silo Wars contender chips,
// and anywhere else we want to expose "save this person/silo for later." Click
// while signed out → sign-up nudge (no save). Click while signed in → toggles
// an interaction row server-side.
//
// Visual: tiny ghost pill; turns coral/filled when starred. Always low-contrast
// so it never competes with primary CTAs on the page.
import { useEffect, useState } from "react";
import { Star } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";

export default function StarSiloButton({ siloId, siloSlug, siloName, signedIn, compact = false, onChange, onRequestFavoriteCTA, ghostStarred = null, onGhostToggle = null }) {
  const [starred, setStarred] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Jun 2026 (Randy) — signed-out state comes from the dots board, not from
  // /is-starred (which needs an account). A ghost star is real enough to light
  // the button and pay its dot; signup converts it server-side.
  useEffect(() => {
    if (signedIn || ghostStarred === null) return;
    setStarred(!!ghostStarred);
  }, [signedIn, ghostStarred]);

  useEffect(() => {
    if (!signedIn || !siloId) return;
    let cancel = false;
    api.get(`/silos/${siloId}/is-starred`)
      .then((r) => { if (!cancel) setStarred(!!r.data?.starred); })
      .catch(() => { /* silent */ });
    return () => { cancel = true; };
  }, [siloId, signedIn]);

  const onClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!signedIn) {
      // Jun 2026 (Randy) — the ghost star. Favoriting signed-out used to open
      // a signup wall, which was deceptive: the row promised a dot and the
      // signup paid its own anyway. Now it stars, pays, and only invites.
      if (onGhostToggle) {
        setLoading(true);
        const next = !starred;
        try {
          const ok = await onGhostToggle(next);
          if (ok !== false) setStarred(next);
        } finally { setLoading(false); }
        return;
      }
      if (onRequestFavoriteCTA) { onRequestFavoriteCTA(); return; }
      toast("Sign up free to save this silo to your interactions.", {
        action: {
          label: "Sign up",
          onClick: () => navigate(`/signup?from_silo=${siloSlug || ""}`),
        },
      });
      return;
    }
    if (!siloId) return;
    setLoading(true);
    const next = !starred;
    setStarred(next); // optimistic
    try {
      await api.post("/interactions/star", { silo_id: siloId, starred: next });
      if (onChange) onChange(next);
      toast.success(next ? `Saved ${siloName || "silo"} to your interactions.` : `Removed ${siloName || "silo"}.`);
    } catch (err) {
      setStarred(!next); // revert
      toast.error(err.response?.data?.detail || "Couldn't save right now.");
    } finally {
      setLoading(false);
    }
  };

  const size = compact ? 14 : 16;
  const padding = compact ? "px-2 py-1" : "px-2.5 py-1.5";
  return (
    <button
      onClick={onClick}
      disabled={loading}
      data-testid={`star-silo-${siloSlug || siloId}`}
      title={starred ? "Remove from your interactions" : "Save this silo to your interactions"}
      className={`${padding} rounded-full inline-flex items-center gap-1.5 text-xs transition-colors ${
        starred
          ? "bg-[#FDF1ED] text-[#C16E5A] border border-[#E8C4B8]"
          : "bg-transparent text-[#6B6A68] hover:text-[#C16E5A] border border-[#E5E5E0] hover:border-[#E8C4B8]"
      } disabled:opacity-60`}
    >
      <Star size={size} weight={starred ? "fill" : "regular"} />
      {!compact && <span>{starred ? "Saved" : "Star"}</span>}
    </button>
  );
}
